import { Component, Inject, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReceivingsService } from '@app/services/receivings/receiving.service';
import { Store } from '@ngrx/store';
import { selectMeState } from '@app/core/store/core.selectors';
import { first, skipWhile } from 'rxjs/operators';
import { ProductsService } from '@app/services/products/product.service';
import { readSerialFailure, readSerialProduct, SerialRefusal } from '../serial-form/serial-form.component';

export interface QuickReceiveDialogData {
  prdID: number;
  storageStructureID: number;
  storagePath: string;
  currentOnHand: number;
  prdNameEn: string;
  // Known by the opener: skips the product fetch (patterns are fetched only for serialized products).
  prdIsSerialized?: number;
  serialPatterns?: string[];
  isInitialStock?: boolean;
}

@Component({
  selector: 'app-quick-receive-dialog',
  templateUrl: './quick-receive-dialog.component.html',
  styleUrls: ['./quick-receive-dialog.component.scss']
})
export class QuickReceiveDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('quantityInput') quantityInput: ElementRef<HTMLInputElement>;
  form: FormGroup;
  isSubmitting: boolean = false;
  // null while the product is loading; serialized products get the serial block instead of the quantity field.
  isSerialized: boolean | null = null;
  patterns: string[] = [];
  serials: string[] = [];
  blocked = false;
  refused: SerialRefusal[] = [];
  failed = false;
  private usrID: number;
  private fTypeID: number;
  private locID: number;

  constructor(
    public dialogRef: MatDialogRef<QuickReceiveDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: QuickReceiveDialogData,
    private fb: FormBuilder,
    private receivingsService: ReceivingsService,
    private productsService: ProductsService,
    private store$: Store
  ) {
    this.form = this.fb.group({
      quantity: [1, [Validators.required, Validators.min(1)]]
    });
  }

  ngOnInit(): void {
    this.store$.select(selectMeState).pipe(
      skipWhile(me => me == null),
      first()
    ).subscribe((me: any) => {
      this.usrID = me.usrID;
      this.fTypeID = me.usrFTypeID;
      this.locID = me.usrLocID;
    });

    this.initProduct();
  }

  productError = false;

  private initProduct(): void {
    const known = this.data.prdIsSerialized;
    if (known != null && (Number(known) !== 1 || this.data.serialPatterns)) {
      this.applyProduct(Number(known) === 1, this.data.serialPatterns || []);
      return;
    }
    this.loadProduct();
  }

  loadProduct(): void {
    this.productError = false;
    this.productsService.getProduct(this.data.prdID).pipe(first()).subscribe({
      next: (res) => {
        const info = readSerialProduct(res);
        this.applyProduct(info.serialized, info.patterns);
      },
      error: () => {
        this.productError = true;
      }
    });
  }

  private applyProduct(serialized: boolean, patterns: string[]): void {
    this.patterns = patterns;
    this.isSerialized = serialized;
    if (!serialized) { this.focusQuantity(); }
  }

  private focusQuantity(): void {
    setTimeout(() => {
      if (this.quantityInput) {
        this.quantityInput.nativeElement.focus();
        this.quantityInput.nativeElement.select();
      }
    });
  }

  ngAfterViewInit(): void {
    this.focusQuantity();
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (this.isSubmitting || this.isSerialized === null) {
      return;
    }
    if (this.isSerialized ? (this.serials.length < 1 || this.blocked) : this.form.invalid) {
      return;
    }

    this.isSubmitting = true;
    this.failed = false;

    const receiveData = {
      prdID: this.data.prdID,
      storageStructureID: this.data.storageStructureID,
      storagePath: this.data.storagePath,
      quantity: this.isSerialized ? this.serials.length : this.form.value.quantity,
      onHand: this.data.currentOnHand,
      usrID: this.usrID,
      fTypeID: this.fTypeID,
      locationID: this.locID,
      isInitialStock: this.data.isInitialStock || false
    } as any;
    if (this.isSerialized) {
      receiveData.serialNumbers = this.serials;
    }

    this.receivingsService.onSave(receiveData as any).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error performing receive:', error);
        this.isSubmitting = false;
        if (this.isSerialized) {
          const failure = readSerialFailure(error);
          this.refused = failure.refused;
          this.failed = failure.failed;
        }
      }
    });
  }
}
